const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const HEALTHCHECK_PATH = '/functions/v1/healthcheck';

interface HealthCheckCache {
  isHealthy: boolean;
  timestamp: number;
  responseTime: number;
}

interface HealthcheckResponse {
  ok: boolean;
  responseTimeMs?: number;
  error?: string;
}

let healthCache: HealthCheckCache | null = null;
const HEALTH_CACHE_DURATION_MS = 30_000;

const isCacheValid = (skipCache: boolean): boolean => {
  if (skipCache || !healthCache) {
    return false;
  }
  return Date.now() - healthCache.timestamp < HEALTH_CACHE_DURATION_MS;
};

const setHealthCache = (isHealthy: boolean, responseTime: number): void => {
  healthCache = { isHealthy, timestamp: Date.now(), responseTime };
};

export const clearHealthCache = (): void => {
  healthCache = null;
};

const invokeHealthcheckEdgeFunction = async (): Promise<{
  ok: boolean;
  errorMessage?: string;
}> => {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    return { ok: false, errorMessage: 'Supabase URL or publishable key not configured' };
  }

  const response = await fetch(`${SUPABASE_URL}${HEALTHCHECK_PATH}`, {
    method: 'GET',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
  });

  const payload = (await response.json().catch(() => null)) as HealthcheckResponse | null;

  if (!response.ok || !payload?.ok) {
    return {
      ok: false,
      errorMessage: payload?.error ?? `Healthcheck failed (${response.status})`,
    };
  }

  return { ok: true };
};

export const checkSupabaseHealth = async (skipCache = false): Promise<boolean> => {
  if (isCacheValid(skipCache)) {
    return healthCache!.isHealthy;
  }

  const startTime = performance.now();

  try {
    const { ok, errorMessage } = await invokeHealthcheckEdgeFunction();
    const responseTime = Math.round(performance.now() - startTime);

    setHealthCache(ok, responseTime);

    if (!ok && import.meta.env.DEV) {
      console.warn('Supabase health check warning:', errorMessage);
    }

    return ok;
  } catch (error) {
    const responseTime = Math.round(performance.now() - startTime);
    const message = error instanceof Error ? error.message : 'Unknown error';

    console.error('Supabase health check failed:', error);
    setHealthCache(false, responseTime);

    if (import.meta.env.DEV) {
      console.warn('Supabase health check error:', message);
    }

    return false;
  }
};
